/**
 * Minimal decoder for protobuf-encoded `foxglove.CompressedVideo` messages.
 *
 * The reader has no general protobuf deserialization support (see serde.ts),
 * but compressed-video topics are commonly recorded with the Foxglove SDK,
 * which writes protobuf. Rather than pull in a protobuf runtime and schema
 * compiler for one fixed, four-field message, this module hand-decodes the
 * wire format.
 *
 * Schema (https://docs.foxglove.dev/docs/sdk/schemas/compressed-video):
 *   field 1: timestamp (message: 1 = sec varint, 2 = nsec varint)
 *   field 2: frame_id (string)
 *   field 3: data (bytes — Annex B-framed video, exactly one frame)
 *   field 4: format (string — "h264", "h265", "vp9", or "av1")
 */

export const FOXGLOVE_COMPRESSED_VIDEO_SCHEMA_NAME = "foxglove.CompressedVideo";

export interface FoxgloveCompressedVideo {
  timestamp: { sec: number; nsec: number };
  frame_id: string;
  data: Uint8Array;
  format: string;
}

// Plain const object (not an enum): wire types are parsed from arbitrary
// message bytes, so comparisons happen against plain numbers.
const WireType = {
  Varint: 0,
  Fixed64: 1,
  LengthDelimited: 2,
  Fixed32: 5,
} as const;

const textDecoder = new TextDecoder();

class WireReader {
  #data: Uint8Array;
  #offset = 0;

  constructor(data: Uint8Array) {
    this.#data = data;
  }

  public get atEnd(): boolean {
    return this.#offset >= this.#data.length;
  }

  public varint(): number {
    let value = 0;
    let shift = 0;
    for (;;) {
      if (this.#offset >= this.#data.length) {
        throw new Error("Truncated protobuf message: unterminated varint");
      }
      const byte = this.#data[this.#offset++];
      value += (byte & 0x7f) * 2 ** shift;
      if ((byte & 0x80) === 0) {
        return value;
      }
      shift += 7;
    }
  }

  public bytes(length: number): Uint8Array {
    if (this.#offset + length > this.#data.length) {
      throw new Error(
        `Truncated protobuf message: ${length} bytes declared, ${this.#data.length - this.#offset} available`,
      );
    }
    const view = this.#data.subarray(this.#offset, this.#offset + length);
    this.#offset += length;
    return view;
  }

  public skip(wireType: number): void {
    switch (wireType) {
      case WireType.Varint:
        this.varint();
        return;
      case WireType.Fixed64:
        this.bytes(8);
        return;
      case WireType.LengthDelimited:
        this.bytes(this.varint());
        return;
      case WireType.Fixed32:
        this.bytes(4);
        return;
      default:
        throw new Error(`Unsupported protobuf wire type ${wireType}`);
    }
  }
}

function decodeTimestamp(data: Uint8Array): { sec: number; nsec: number } {
  const reader = new WireReader(data);
  const timestamp = { sec: 0, nsec: 0 };
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    if (fieldNumber === 1 && wireType === WireType.Varint) {
      timestamp.sec = reader.varint();
    } else if (fieldNumber === 2 && wireType === WireType.Varint) {
      timestamp.nsec = reader.varint();
    } else {
      reader.skip(wireType);
    }
  }
  return timestamp;
}

export function decodeCompressedVideoMessage(
  view: ArrayBufferView,
): FoxgloveCompressedVideo {
  const reader = new WireReader(
    new Uint8Array(view.buffer, view.byteOffset, view.byteLength),
  );
  const message: FoxgloveCompressedVideo = {
    timestamp: { sec: 0, nsec: 0 },
    frame_id: "",
    data: new Uint8Array(),
    format: "",
  };
  while (!reader.atEnd) {
    const tag = reader.varint();
    const fieldNumber = tag >>> 3;
    const wireType = tag & 0x07;
    if (wireType !== WireType.LengthDelimited) {
      reader.skip(wireType);
      continue;
    }
    const value = reader.bytes(reader.varint());
    switch (fieldNumber) {
      case 1:
        message.timestamp = decodeTimestamp(value);
        break;
      case 2:
        message.frame_id = textDecoder.decode(value);
        break;
      case 3:
        message.data = value;
        break;
      case 4:
        message.format = textDecoder.decode(value);
        break;
      default:
        break;
    }
  }
  return message;
}
